import * as angular from "angular";
import {
    FileItem,
    FileUploader,
    FileUploaderFactory,
} from "angular-file-upload";
import { CredentialsManager } from "../shared/credentials-manager";
import { DocumentService } from "../services/document.service";
import { FileType } from "../utils/interfaces";
import { DocumentModel } from "../models/document.model";
import { fileConstants } from "../utils/constants/files.constants";

export class DocumentDataProvider {
    public documents: DocumentModel[] | null = null;
    private inProgressDocuments: DocumentModel[] | null = null;
    private getDocumentsPromise: Promise<any> = null;
    public inProgressCount: number = 0;
    private activatedContent: boolean = true; //used to dismiss the document popover
    private hideInProgressCard: boolean;
    private credentialsManager: CredentialsManager;
    private fileUpLoader: FileUploaderFactory;
    private resource: DocumentService;
    private modelsUtils: any;
    private $timeout: any;
    private $q: any;
    private $cookies: any;
    private __env: any;
    private filteredTypes: string[] = [];
    public uploaders: object = {};
    constructor(
        $q: any,
        FileUploader: FileUploaderFactory,
        CredentialsManager: CredentialsManager,
        DocumentService: DocumentService,
        ModelsUtils: any,
        $timeout: any,
        $cookies: any,
        __env: any
    ) {
        this.$q = $q;
        this.credentialsManager = CredentialsManager;
        this.resource = DocumentService;
        this.modelsUtils = ModelsUtils;
        this.fileUpLoader = FileUploader;
        this.$timeout = $timeout;
        this.$cookies = $cookies;
        this.__env = __env;
        this.hideInProgressCard =
            this.$cookies.get("hideInProgressCard") === "true";
    }

    clearAll(): void {
        this.documents = null;
        this.inProgressDocuments = null;
        this.getDocumentsPromise = null;
        this.uploaders = {};
    }

    deleteDocument(document: DocumentModel): Promise<any> {
        const deferred = this.$q.defer();
        this.resource.delete({ id: document.dcid }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.spliceDocument(document);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    setHideInProgressCard(bool: boolean): void {
        this.hideInProgressCard = bool;
        this.$cookies.put("hideInProgressCard", bool, {
            domain: "." + this.__env.host,
            path: "/",
        });
    }

    spliceDocument(document: DocumentModel): void {
        if (this.documents) {
            let index = this.documents.indexOf(document);
            if (index > -1) {
                this.documents.splice(index, 1);
            }
        }
    }

    getInProgressDocuments(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.inProgressDocuments === null) {
            this.getDocuments().then(() => {
                this.inProgressDocuments = this.documents.filter((document) => {
                    return document.dccompleted === "0";
                });
                this.inProgressCount = this.inProgressDocuments.length;
                deferred.resolve(this.inProgressDocuments);
            });
        } else {
            deferred.resolve(this.inProgressDocuments);
        }
        return deferred.promise;
    }

    getDocumentsByType(dccid: string): Promise<any> {
        const deferred = this.$q.defer();
        this.getDocuments().then(() => {
            let documentsOfType = this.documents.filter((document) => {
                return document.dccid === dccid;
            });
            deferred.resolve(documentsOfType);
        });
        return deferred.promise;
    }

    getDocuments(reload?: boolean): Promise<any> {
        const deferred = this.$q.defer();
        if (this.documents !== null && !reload) {
            deferred.resolve(this.documents);
            this.getDocumentsPromise = deferred.promise;
            return this.getDocumentsPromise;
        } else if (this.getDocumentsPromise) {
            return this.getDocumentsPromise;
        } else {
            this.documents = [];
            this.resource.query({}).then(
                (response: object) => {
                    const json = response["data"];
                    for (let i = 0; i < json.data.length; i++) {
                        this.documents.push(
                            this.modelsUtils.document(json.data[i])
                        );
                    }
                    deferred.resolve(this.documents);
                },
                (error: object) => deferred.reject(error)
            );
            this.getDocumentsPromise = deferred.promise;
            return this.getDocumentsPromise;
        }
    }

    allFilteredTypes(fileTypes: FileType[], getAll: boolean = false): string[] {
        this.filteredTypes = [];
        angular.forEach(fileTypes, (value: FileType) => {
            if (value.active || getAll)
                this.filteredTypes = this.filteredTypes.concat(value.types);
        });
        return this.filteredTypes;
    }

    documentWithID(dcid: string): DocumentModel {
        if (this.documents) {
            for (let i = 0; i < this.documents.length; i++) {
                if (this.documents[i].dcid == dcid) {
                    return this.documents[i];
                }
            }
        }
        return null;
    }

    replaceDocument(document: DocumentModel): void {
        this.$timeout(() => {
            let existingDocument = this.documentWithID(document.dcid);
            if (existingDocument) {
                let inProgress = existingDocument.inProgress();
                angular.extend(existingDocument, document);
                if (inProgress && !existingDocument.inProgress()) {
                    //Keep document in inProgress array so it stays in the box as completed but decrement the count so it can now be dismissed
                    this.inProgressCount--;
                }
            } else {
                if (!this.documents) {
                    this.documents = [];
                }
                this.documents.unshift(document);
                if (document.inProgress() && this.inProgressDocuments) {
                    this.inProgressCount++;
                    this.inProgressDocuments.unshift(document);
                }
                //check in progress. add to filered docs
            }
        });
    }

    processSocketUpdate(socketData: any): void {
        if (this.documents) {
            this.replaceDocument(this.modelsUtils.document(socketData));
        }
    }

    processSocketCreate(socketData: any): void {
        if (this.documents) {
            this.replaceDocument(this.modelsUtils.document(socketData));
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.documents) {
            let existingDocument = this.documentWithID(socketData.dcid);
            if (existingDocument) {
                this.spliceDocument(existingDocument);
            }
        }
    }

    closeInProgress(): void {
        if (this.inProgressDocuments) {
            angular.forEach(
                this.inProgressDocuments,
                (doc: DocumentModel, index: number) => {
                    if (!doc.inProgress())
                        this.inProgressDocuments.splice(index, 1);
                }
            );
        }
    }

    getFileUploader(dccid: number): FileUploader {
        //each category must have its down uploader because the urls are different.
        if (this.uploaders.hasOwnProperty(dccid)) return this.uploaders[dccid];

        let uploader = new this.fileUpLoader({
            url:
                "/api/document/create/?campaignid=" +
                this.credentialsManager.campaignid +
                "&dccid=" +
                dccid,
            headers: {
                Authorization:
                    "Bearer " + this.credentialsManager.getAccessToken(),
            } as any,
            autoUpload: true,
        });

        uploader.onCompleteItem = (fileItem: FileItem, response: object) => {
            this.inProgressCount--;
            if (response["success"]) {
                this.replaceDocument(
                    this.modelsUtils.document(response["data"])
                );
            }
        };

        uploader.onBeforeUploadItem = () => {
            this.setHideInProgressCard(false);
            this.inProgressCount++;
        };

        uploader.filters.push({
            name: "customFilter",
            fn: (item: any) => {
                let ext = item.name.substr(item.name.lastIndexOf(".") + 1);
                if (dccid === 4) {
                    // Maximum file size is 20MB for Map Overlay
                    return (
                        "|kml|kmz|geojson|".indexOf(ext) !== -1 &&
                        item.size <= 20 * 1024 * 1024
                    );
                } else {
                    // Maximum file size is 500MB
                    return (
                        fileConstants.allAcceptedFileTypes.indexOf(ext) !==
                            -1 && item.size <= 524288000
                    );
                }
            },
        });

        this.uploaders[dccid] = uploader;
        return uploader;
    }
}

angular
    .module("vcDataProvider")
    .service("DocumentDataProvider", DocumentDataProvider);
